import { Loader2, Sparkles } from 'lucide-react';
import {
  RadixSelect,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectGroup,
  SelectLabel,
  SelectItem,
  Badge,
} from '@/components/ui';
import PluginPicker from './PluginPicker';
import type { ExploreCandidate, ExploreResponse } from '../../types';

interface ExploreResultPanelProps {
  result: ExploreResponse;
  selectedRef: string;
  onRefChange: (ref: string) => void;
  selectedSubdir: string | undefined;
  onCandidateSelect: (candidate: ExploreCandidate) => void;
  manualSubdir: string;
  onManualSubdirChange: (value: string) => void;
  /** Show a spinner overlay on the picker while a re-explore is in flight. */
  reexploring?: boolean;
}

export default function ExploreResultPanel({
  result,
  selectedRef,
  onRefChange,
  selectedSubdir,
  onCandidateSelect,
  manualSubdir,
  onManualSubdirChange,
  reexploring = false,
}: ExploreResultPanelProps) {
  return (
    <div className="space-y-4">
      {result.marketplace && (
        <div className="flex items-start gap-3 rounded-md border border-border bg-accent/30 px-3 py-2.5">
          <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-foreground/70" />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-foreground">
                {result.marketplace.name}
              </span>
              <Badge variant="info">marketplace.json</Badge>
            </div>
            {result.marketplace.description && (
              <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
                {result.marketplace.description}
              </p>
            )}
          </div>
        </div>
      )}

      <div className="space-y-1.5">
        <label className="text-sm font-medium text-foreground">Ref</label>
        <RadixSelect value={selectedRef} onValueChange={onRefChange}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Choose a branch or tag" />
          </SelectTrigger>
          <SelectContent>
            {result.branches.length > 0 && (
              <SelectGroup>
                <SelectLabel>Branches</SelectLabel>
                {result.branches.map((b) => (
                  <SelectItem key={`branch:${b}`} value={b}>
                    {b}
                    {b === result.defaultBranch && (
                      <span className="ml-2 text-xs text-muted-foreground">
                        default
                      </span>
                    )}
                  </SelectItem>
                ))}
              </SelectGroup>
            )}
            {result.tags.length > 0 && (
              <SelectGroup>
                <SelectLabel>Tags</SelectLabel>
                {result.tags.map((t) => (
                  <SelectItem key={`tag:${t}`} value={t}>
                    {t}
                  </SelectItem>
                ))}
              </SelectGroup>
            )}
          </SelectContent>
        </RadixSelect>
        <p className="font-mono text-[11px] text-muted-foreground">
          {result.commitSha.slice(0, 7)} · {result.refType}
        </p>
      </div>

      <div className="space-y-1.5">
        <label className="text-sm font-medium text-foreground">
          Plugin
          {result.candidates.length > 1 && (
            <span className="ml-2 text-xs font-normal text-muted-foreground">
              ({result.candidates.length} discovered)
            </span>
          )}
        </label>
        <div className="relative">
          <PluginPicker
            candidates={result.candidates}
            selectedSubdir={selectedSubdir}
            onSelect={onCandidateSelect}
            manualSubdir={manualSubdir}
            onManualSubdirChange={onManualSubdirChange}
          />
          {reexploring && (
            <div className="absolute inset-0 flex items-center justify-center rounded-md bg-background/70">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          )}
        </div>
      </div>

      {result.warnings && result.warnings.length > 0 && (
        <ul className="space-y-1 rounded-md border border-border bg-muted/30 px-3 py-2 text-[11px] text-muted-foreground">
          {result.warnings.map((w, i) => (
            <li key={i}>· {w}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
